import {
  ChatBubbleIcon,
  InfoCircledIcon,
  PaperPlaneIcon,
} from "@radix-ui/react-icons";
import { BubbleTone } from "components/IconBubble";
import SectionTitle from "components/SectionTitle";
import { Dialog } from "components/Dialog";
import { DialogTrigger } from "@radix-ui/react-dialog";
import { Button, ButtonColor, ButtonFill, ButtonSize } from "components/Button";

import {
  Auth0AccountRole,
  ParentLimitedResponse,
  StudentLimitedResponse,
  TutorLimitedResponse,
  MessageResponse,
} from "client/openapi";
import { Tooltip, TooltipContent, TooltipTrigger } from "components/Tooltip";
import "../index.css";
import { useContext, useState } from "react";
import { OrgRolesAndAccountContext } from "util/OrgRolesAccountContext";
import { findOrgIdsByRole } from "util/accounts";
import CommunicationDialog from "components/CommunicationDialog";
import ComposeMessageDialog from "components/Dashboard/ComposeMessageDialog";

export default function Communication({
  title,
  parent,
  student,
  tutor,
  toolTipMessage,
}: {
  title: string;
  parent?: ParentLimitedResponse;
  student?: StudentLimitedResponse;
  tutor?: TutorLimitedResponse;
  toolTipMessage?: string;
}) {
  const { all_account_permissions } = useContext(OrgRolesAndAccountContext);
  const [newMessageOpen, setNewMessageOpen] = useState<boolean>(false);
  const [messages, setMessages] = useState<MessageResponse[]>([]);
  const orgId = (student?.org_id || tutor?.org_id || parent?.org_id) as number;

  // A tutor who is not an admin of the organization sees only their own
  // messages with a student or parent.
  const isAdmin = [
    Auth0AccountRole.ORG_ADMIN,
    Auth0AccountRole.TADPOLE_ADMIN,
  ].some((role) =>
    findOrgIdsByRole(all_account_permissions ?? {}, role).includes(orgId)
  );
  const tooltip =
    all_account_permissions && !isAdmin && (student || parent)
      ? `These are your messages with this ${student ? "student" : "parent"}.`
      : toolTipMessage;

  return (
    <section className="profile-box--section" id="parent-communication">
      <div className="profile-box--section-header pt-10">
        <div className="flex flex-row items-center">
          <SectionTitle icon={<ChatBubbleIcon />} tone={BubbleTone.SKY}>
            {title}
          </SectionTitle>
          {tooltip && (
            <div className="ml-2 mr-2">
              <Tooltip>
                <TooltipTrigger>
                  <InfoCircledIcon className="w-5 h-5 text-gray-400" />
                </TooltipTrigger>
                <TooltipContent>{tooltip}</TooltipContent>
              </Tooltip>
            </div>
          )}
        </div>

        <Dialog open={newMessageOpen} onOpenChange={setNewMessageOpen}>
          <DialogTrigger>
            <Button
              size={ButtonSize.EXTRA_SMALL}
              color={ButtonColor.DARK_BLUE}
              fill={ButtonFill.HOLLOW}
            >
              Send Message <PaperPlaneIcon />
            </Button>
          </DialogTrigger>
          {newMessageOpen && (
            <ComposeMessageDialog
              students={student ? [student] : undefined}
              tutors={tutor ? [tutor] : undefined}
              parents={parent ? [parent] : undefined}
              org_id={orgId}
            />
          )}
        </Dialog>
      </div>

      <div className="profile-box--section-items">
        <div className="profile-box--section-item">
          <CommunicationDialog
            parent={parent}
            student={student}
            tutor={tutor}
            messages={messages}
            setMessages={setMessages}
          />
        </div>
      </div>
    </section>
  );
}
